import { useEffect, useState } from 'react';
import { i18n } from '@lingui/core';
import { t, Trans } from '@lingui/macro';
import _ from 'lodash';
import { Box, Button, MenuItem } from '@material-ui/core';
import { useFormContext } from 'react-hook-form';
import Typography from '@material-ui/core/Typography';
import FormSection from '../../form/FormSection';
import NgpToggle from '../../form/NgpToggle';
import NgpTextField from '../../form/NgpTextField';
import {
    AGENT_CERTIFICATE_NAME_FIELD,
    AGENT_CERTIFICATE_FIELD,
    AGENT_SSID_FIELD,
    AGENT_STATUS_FIELD,
    AGENT_STATUS_RUNNING,
    AGENT_WIFI_OPTIONS,
    AGENT_WIFI_SELECTION_FIELD,
    AGENT_WIFI_SINGLE_SSID_OPTION,
    AGENTS_FUNCTION_NAME,
    AGENT_NETWORK_BSSID_FIELD,
    DBM,
    AGENT_WIFI_PERMACADDR_ENABLED,
    AGENT_WIFI_STATIC_IP_ENABLED,
    AGENT_WIFI_STATIC_IP_ADDRESS,
    AGENT_WIFI_STATIC_IP_DNS,
    AGENT_WIFI_STATIC_IP_GATEWAY,
    AGENT_WIFI_STATIC_IP_MASK,
    AGENT_WIFI_MULTI_SSID_OPTION,
} from './AgentConstants';
import NgpSelect from '../../form/NgpSelect';
import { buildCertDataSource, buildCertificateColumns, buildWiFiNetworkOptions } from './AgentDetailHelper';
import { COLUMN_TYPES } from '../../data-table/DataTableColumns';
import DataTable from '../../data-table/DataTable';
import { useArrayLoader, useQueryLoader } from '../../data-table/Loaders';
import { useIntervalRefreshTrigger } from '../../data-table/Triggers';
import { ID_FIELD } from '../../form/FormConstants';
import { SORT_DESC, isIpValid, isDNSValid, isNetworkPrefixLengthValid } from '../../../utilities/FormUtilities';
import { renderWifiSSID } from './AgentRenderHelper';
import { useColumnStyles } from './AgentStyles';
import { useIsDarkMode } from '../../../stores/SessionStore';
import NgpTextarea from '../../form/NgpTextarea';
import { CERTIFICATE_USAGES } from '../certificates/CertificateConstants';
import { AUTH_CERTIFICATE_FIELD, EAP_TYPE_FIELD, EAP_TYPE_OPTIONS } from '../wifi-networks/WifiNetworkConstants';
import { useDataService } from '../../../services/ApiDataService';

const BSSID_FIELD = 'bssid';
const SSID_FIELD = 'ssid';
const CHANNEL_FIELD = 'channel';
const SIGNAL_LEVEL_DBM_FIELD = 'signal_level_dbm';

function renderWiFiIcon(row, column, latestAgent) {
    const connectedToBSSID = _.get(latestAgent, 'wifiBssid');
    if (!connectedToBSSID) {
        return null;
    }

    if (
        row[SSID_FIELD] !== _.get(latestAgent, 'wifiSsid') ||
        row[CHANNEL_FIELD] !== _.get(latestAgent, 'wifiChannel') ||
        row[BSSID_FIELD] !== connectedToBSSID
    ) {
        return null;
    }

    return renderWifiSSID(latestAgent, column, false);
}

function buildBSSIDColumns(columnClasses, latestAgent) {
    return [
        {
            type: COLUMN_TYPES.TEXT,
            sortable: false,
            mapping: 'wifiSsid',
            className: columnClasses.root,
            width: 35,
            renderer: (row, column) => renderWiFiIcon(row, column, latestAgent),
        },
        {
            type: COLUMN_TYPES.TEXT,
            title: t`BSSID`,
            sortable: true,
            mapping: BSSID_FIELD,
            width: 135,
        },
        {
            type: COLUMN_TYPES.TEXT,
            title: t`SSID`,
            sortable: true,
            mapping: SSID_FIELD,
            width: 300,
        },
        {
            type: COLUMN_TYPES.TEXT,
            title: t`Wi-Fi Signal Strength`,
            sortable: true,
            mapping: SIGNAL_LEVEL_DBM_FIELD,
            formatter: (value) => `${value} ${DBM}`,
            width: 180,
        },
        {
            type: COLUMN_TYPES.NUMERIC,
            title: t`Channel`,
            sortable: true,
            mapping: CHANNEL_FIELD,
            align: 'center',
            width: 100,
        },
        {
            type: COLUMN_TYPES.TEXT,
            title: t`Channel Frequency`,
            sortable: true,
            mapping: 'frequency',
            width: 200,
            formatter: (value) => `${value}GHz`,
        },
    ];
}

const listRefreshInterval = 30000;

function buildBSSIDDataSource(agentId) {
    return {
        url: `/query/admin/agents/${agentId}/visibleWiFiNetworks`,
        totalProperty: 'count',
        dataProperty: 'data',
        params: {
            searchFields: [BSSID_FIELD, SSID_FIELD, CHANNEL_FIELD],
        },
    };
}

const initialBssidSort = [{ property: SIGNAL_LEVEL_DBM_FIELD, direction: SORT_DESC }];
const initialCertSort = [{ property: AGENT_CERTIFICATE_NAME_FIELD, direction: SORT_DESC }];
const showNetworksText = i18n._('Show Visible Wi-Fi Networks');
const hideNetworksText = i18n._('Hide Visible Wi-Fi Networks');
const noCertificateText = i18n._(
    t`Note: No certificate configured. Please configure the certificate for the selected Wifi network, or select the certificate for nPoint.`
);

const invalidIpText = i18n._(t`Please enter a valid IP address`);
const invalidNetworkPrefixText = i18n._(t`Please enter a valid network prefix length: 0-32`);
const invalidDNSText = i18n._(t`Please enter a list of DNS IP addresses separated by ';'`);
export function validateIp(value) {
    if (!value) {
        return true;
    }

    if (!isIpValid(value)) {
        return i18n._(invalidIpText);
    }

    return true;
}

export function validateNetworkPrefix(value) {
    if (!value) {
        return true;
    }
    if (!isNetworkPrefixLengthValid(value)) {
        return i18n._(invalidNetworkPrefixText);
    }
    return true;
}

export function validateDNS(value) {
    if (!value) {
        return true;
    }
    if (!isDNSValid(value)) {
        return i18n._(invalidDNSText);
    }
    return true;
}

export function AgentDetailWiFi({
    formData,
    classes,
    agentHasWireless,
    configuredWiFiNetworks,
    latestAgentData,
    isHardwareEdgeSensorAgent,
}) {
    const columnClasses = useColumnStyles();
    const dataService = useDataService();
    const showWiFiSection = agentHasWireless.current;
    const { watch, getValues, setValue } = useFormContext();
    const wifiSelection = watch(AGENT_WIFI_SELECTION_FIELD);
    const ssidSelection = watch(AGENT_SSID_FIELD);
    const wifiNetworkOptions = buildWiFiNetworkOptions(configuredWiFiNetworks.current, formData);
    const bssidLoader = useQueryLoader(buildBSSIDDataSource(formData[ID_FIELD]), { disableFilterState: true });
    const { triggerRef } = useIntervalRefreshTrigger({
        interval: listRefreshInterval,
    });
    const [showWifiNetworks, setShowWifiNetworks] = useState(false);
    const [wifiNPointLevelCert, setWifiNPointLevelCert] = useState([]);
    const [wifiNetworkCert, setWifiNetworkCert] = useState([]);
    const [showAssociatedCert, setShowAssociatedCert] = useState([]);
    const [showNoCertificateText, setShowNoCertificateText] = useState([]);
    const bssidColumns = buildBSSIDColumns(columnClasses, latestAgentData);
    const certificateColumns = buildCertificateColumns();
    // When an agent is using multi-mode they don't pass up wireless, which makes showing that selection
    //  just based off their Wi-Fi selection doesnt' make sense because it will be empty, the nPoint has to
    //  be in single-mode for that list to populate.
    const showWiFiNetworks =
        _.get(latestAgentData, AGENT_STATUS_FIELD) === AGENT_STATUS_RUNNING &&
        wifiSelection === AGENT_WIFI_SINGLE_SSID_OPTION;

    const handleWifiNetworksClick = () => {
        setShowWifiNetworks((prev) => !prev);
    };
    const [isDarkMode] = useIsDarkMode();
    const wifiButtonColor = isDarkMode ? 'secondary' : 'primary';
    const rules = {
        required: false,
        validate: {
            format: (value) => {
                if (validateBSSID(value)) {
                    return true;
                }
                return 'Invalid BSSID Format';
            },
        },
    };
    const initialSsid = _.get(formData, AGENT_SSID_FIELD);
    const currentSsid = getValues(AGENT_SSID_FIELD);

    const selectedWifiNetwork = wifiNetworkOptions.find((network) => network._id === ssidSelection);
    const isEapTypeTls = _.get(selectedWifiNetwork, EAP_TYPE_FIELD) === EAP_TYPE_OPTIONS.eapTls;

    useEffect(() => {
        (async () => {
            const { url: nPointLevelUrl, params: nPointLevelParams } = buildCertDataSource(
                _.get(latestAgentData, AGENT_CERTIFICATE_FIELD),
                CERTIFICATE_USAGES.wifi
            );
            const wifiNPointLevelCertData = await dataService.get(nPointLevelUrl, nPointLevelParams);
            let wifiNetworkCertData = null;
            if (_.get(selectedWifiNetwork, AUTH_CERTIFICATE_FIELD)) {
                const { url: wifiNetworkUrl, params: wifiNetworkParams } = buildCertDataSource(
                    [_.get(selectedWifiNetwork, AUTH_CERTIFICATE_FIELD)],
                    CERTIFICATE_USAGES.wifi
                );
                wifiNetworkCertData = await dataService.get(wifiNetworkUrl, wifiNetworkParams);
            }

            setWifiNPointLevelCert(wifiNPointLevelCertData?.data);
            setWifiNetworkCert(wifiNetworkCertData?.data);

            setShowAssociatedCert(
                wifiSelection === AGENT_WIFI_SINGLE_SSID_OPTION &&
                    isEapTypeTls &&
                    ((!_.isNil(wifiNPointLevelCertData?.data) && wifiNPointLevelCertData?.data.length !== 0) ||
                        (!_.isNil(wifiNetworkCertData?.data) && wifiNetworkCertData?.data.length !== 0))
            );

            setShowNoCertificateText(
                wifiSelection === AGENT_WIFI_SINGLE_SSID_OPTION &&
                    isEapTypeTls &&
                    (_.isNil(wifiNPointLevelCertData?.data) || wifiNPointLevelCertData?.data.length === 0) &&
                    (_.isNil(wifiNetworkCertData?.data) || wifiNetworkCertData?.data.length === 0)
            );
        })();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [currentSsid]);

    const certLoader = useArrayLoader(
        !_.isNil(wifiNPointLevelCert) && wifiNPointLevelCert.length !== 0 ? wifiNPointLevelCert : wifiNetworkCert,
        (data) => data
    );

    if (!showWiFiSection) return null;

    const staticIpEnabled = watch(AGENT_WIFI_STATIC_IP_ENABLED);
    const ipRules = {
        validate: (value) => validateIp(value),
    };
    const networkPrefixRules = {
        validate: (value) => validateNetworkPrefix(value),
    };
    const dnsRules = {
        validate: (value) => validateDNS(value),
    };

    return (
        <FormSection title={<Trans>Wi-Fi Configuration</Trans>}>
            <NgpSelect
                fieldName={AGENT_WIFI_SELECTION_FIELD}
                label={<Trans>Wi-Fi</Trans>}
                required
                className={classes.genericSelect}
            >
                {_.map(AGENT_WIFI_OPTIONS, (value, key) => (
                    <MenuItem key={key} value={key}>
                        {value}
                    </MenuItem>
                ))}
            </NgpSelect>

            {wifiSelection === AGENT_WIFI_SINGLE_SSID_OPTION && (
                <NgpSelect
                    fieldName={AGENT_SSID_FIELD}
                    label={<Trans>Assigned Wi-Fi Network Profiles</Trans>}
                    required
                    className={classes.genericSelect}
                >
                    {_.map(wifiNetworkOptions, (network) => (
                        <MenuItem key={network._id} value={network._id}>
                            {network.nameFmt}
                        </MenuItem>
                    ))}
                </NgpSelect>
            )}

            {wifiSelection === AGENT_WIFI_SINGLE_SSID_OPTION && (
                <NgpTextarea
                    title="Assigned BSSID (Optional) Title"
                    fieldName={AGENT_NETWORK_BSSID_FIELD}
                    label="Assigned BSSID: (Optional) "
                    cols={45}
                    inputProps={{ maxLength: 17 }}
                    fieldRules={rules}
                    helpTipText="This assigns the nPoint - not the selected WiFi network - to use the specified BSSID. If you change networks, you will need to reasign the BSSID to a valid one for the new network. Leave blank for automatic selection."
                />
            )}

            {initialSsid !== currentSsid && setValue(AGENT_NETWORK_BSSID_FIELD, '')}
            {(typeof formData.wifiBssid === 'undefined' || formData.wifiBssid === null) && <p>*Not Connected*</p>}
            {formData.wifiBssid !== null && typeof formData.wifiBssid !== 'undefined' && (
                <p>Connected to {formData.wifiBssid}</p>
            )}
            {isHardwareEdgeSensorAgent.current === true &&
                (wifiSelection === AGENT_WIFI_SINGLE_SSID_OPTION || wifiSelection === AGENT_WIFI_MULTI_SSID_OPTION) && (
                    <NgpToggle
                        fieldName={AGENT_WIFI_PERMACADDR_ENABLED}
                        label={<Trans>Enable Permanent MAC address</Trans>}
                    />
                )}
            {wifiSelection === AGENT_WIFI_SINGLE_SSID_OPTION && isHardwareEdgeSensorAgent.current === true && (
                <>
                    <NgpToggle fieldName={AGENT_WIFI_STATIC_IP_ENABLED} label={<Trans>Use Static IP</Trans>} />

                    {staticIpEnabled === true && (
                        <>
                            <NgpTextField
                                fieldName={AGENT_WIFI_STATIC_IP_ADDRESS}
                                required
                                label={<Trans>IP Address</Trans>}
                                fieldRules={ipRules}
                                helpTipText="This assigns the static ip address in the asigned Wifi network."
                            />
                            <NgpTextField
                                fieldName={AGENT_WIFI_STATIC_IP_MASK}
                                label={<Trans>Network Prefix Length</Trans>}
                                required
                                fieldRules={networkPrefixRules}
                                helpTipText="This assigns the network prefix length for the configured IP address. The value must be in the 0-32 range."
                            />
                            <NgpTextField
                                fieldName={AGENT_WIFI_STATIC_IP_GATEWAY}
                                required
                                label={<Trans>Gateway</Trans>}
                                fieldRules={ipRules}
                                helpTipText="This assigns the gateway ip address in the asigned Wifi network."
                            />
                            <NgpTextField
                                fieldName={AGENT_WIFI_STATIC_IP_DNS}
                                label={<Trans>DNS</Trans>}
                                inputProps={{ size: 50, maxLength: 1000 }}
                                fieldRules={dnsRules}
                                required
                                helpTipText="This assigns the DNS in the asigned Wifi network. Multiple DNS should be separated by ';'."
                            />
                        </>
                    )}
                </>
            )}

            {showWiFiNetworks && (
                <Box mt={4}>
                    <Box mb={2.5}>
                        <Button
                            variant="outlined"
                            size="small"
                            color={wifiButtonColor}
                            onClick={handleWifiNetworksClick}
                        >
                            {showWifiNetworks ? hideNetworksText : showNetworksText}
                        </Button>
                    </Box>

                    {showWifiNetworks && (
                        <DataTable
                            tableId={`${AGENTS_FUNCTION_NAME}-bssids`}
                            triggerRef={triggerRef}
                            loader={bssidLoader}
                            columns={bssidColumns}
                            sorters={initialBssidSort}
                            visibleRows={10}
                            disableCSVExport
                            disableColumnRemover
                        />
                    )}
                </Box>
            )}

            <Box mt={3}>
                {showNoCertificateText && (
                    <Typography variant="body2">
                        <b>{noCertificateText}</b>
                    </Typography>
                )}
            </Box>

            {showAssociatedCert && (
                <FormSection title={<Trans>Associated Wifi Certificates</Trans>}>
                    <DataTable
                        tableId={`${AGENTS_FUNCTION_NAME}-certificate`}
                        triggerRef={triggerRef}
                        loader={certLoader}
                        columns={certificateColumns}
                        sorters={initialCertSort}
                        visibleRows={10}
                        disableCSVExport
                        disableColumnRemover
                        showToolbar={false}
                        showSearch={false}
                    />
                </FormSection>
            )}
        </FormSection>
    );
}

function validateBSSID(bssid) {
    if (bssid === '') {
        return true;
    }

    const bssidFormat = new RegExp('^[0-9a-f]{2}(:[0-9a-f]{2}){5}$', 'i');

    return bssidFormat.test(bssid);
}
